/**
 * Single source of truth for the Hey Be Safe browser-extension store URLs and
 * a tiny user-agent sniffer so clients can surface the right "install the
 * extension" link. Used by the web onboarding wizard; safe to reuse anywhere.
 */

export type ExtensionBrowser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

/**
 * Canonical listing URLs per store.
 *
 * Edge has its own published listing and must NOT be pointed at the Chrome Web
 * Store. The CWS listing does install in Edge, which is why this used to be one
 * URL, but it is the wrong store to send an Edge user to and it has a concrete
 * cost: an Edge Add-ons install gets a DIFFERENT extension id, so the web app's
 * install probe looks for the wrong `chrome-extension://` origin and the
 * "install the extension" onboarding step never ticks itself off. The two facts
 * have to move together — see EXTENSION_PROBE_IDS in `extensionPresence.ts`.
 */
export const EXTENSION_STORE_URLS = {
  chrome: 'https://chromewebstore.google.com/detail/hey-be-safe/jjehmmclfmdkoimajiclfieajgoclhde',
  edge: 'https://microsoftedge.microsoft.com/addons/detail/hey-be-safe/jbhenilijgkmpncklfondjibojcbgphn',
  firefox: 'https://addons.mozilla.org/en-US/firefox/addon/hey-be-safe/',
} as const;

/**
 * Best-effort browser detection from a user-agent string. Order matters: Edge
 * (`Edg/`) and Firefox must be tested before Chrome because Chromium-based
 * browsers also carry `Chrome` in their UA. Defaults to the `userAgent` of the
 * current environment when no argument is passed; returns `'other'` when no UA
 * is available (e.g. SSR / tests with no navigator).
 */
export function detectBrowser(
  userAgent: string | undefined =
    typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
): ExtensionBrowser {
  if (!userAgent) return 'other';
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/') || ua.includes('edga/') || ua.includes('edgios/')) return 'edge';
  if (ua.includes('firefox/') || ua.includes('fxios/')) return 'firefox';
  // Safari must be checked after Chrome/Edge/Firefox since they all include "safari".
  if (ua.includes('chrome/') || ua.includes('chromium/') || ua.includes('crios/')) return 'chrome';
  if (ua.includes('safari/')) return 'safari';
  return 'other';
}

/**
 * The store URL to point a given browser at. Firefox → AMO, Edge → Edge Add-ons,
 * everything else → the Chrome Web Store, which is the broadest fit. Safari has
 * no published listing yet, so it falls back to Chrome.
 */
export function extensionStoreUrl(browser: ExtensionBrowser = detectBrowser()): string {
  if (browser === 'firefox') return EXTENSION_STORE_URLS.firefox;
  if (browser === 'edge') return EXTENSION_STORE_URLS.edge;
  return EXTENSION_STORE_URLS.chrome;
}
